import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import { GithubOnboardingStyles } from 'pages/provisioning/day2/github-onboarding-new/style';

const StyledTooltip = styled(({ className, ...props }) => <Tooltip {...props} arrow classes={{ popper: className }} />, {
    shouldForwardProp: (prop) => !['maxwidth', 'tooltipSx', 'arrowSize'].includes(prop),
})(({ theme, maxwidth, tooltipSx, arrowSize }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.white,
        fontSize: arrowSize || undefined,
        '&::before': {
            boxShadow: 'none',
            border: 0,
        },
    },
    [`& .${tooltipClasses.tooltip}`]: {
        fontSize: 12,
        lineHeight: 1.4,
        textAlign: 'left',
        whiteSpace: 'normal',
        padding: '8px 10px',
        borderRadius: 6,
        width: 'auto',
        maxWidth: maxwidth || 320,
        backgroundColor: theme.palette.white,
        color: theme?.palette?.black?.dark,
        border: '1px solid #E9E9E9',
        boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
        ...(tooltipSx || {}),
    },
}));

const GenericTooltipText = ({
    text,
    showTooltip = true,
    tooltipContent,
    isTypography = false,
    isTextField = false,
    placement,
    maxWidth,
    tooltipSx,
    arrowSize,
    variant,
    ellipsis,
    sx,
    tooltipProps,
    textFieldProps,
    children,
    dataTestId,
    isStyledTooltip = false,
}) => {
    const ellipsisStyles = isTypography && ellipsis ? GithubOnboardingStyles.ellipsisStyle : undefined;

    const getElement = React.useMemo(() => {
        if (children) return children;

        if (isTextField) {
            return <TextField {...textFieldProps} />;
        }

        if (isTypography) {
            return (
                <Typography variant={variant} sx={{ ...ellipsisStyles, ...sx }}>
                    {text}
                </Typography>
            );
        }
        return null;
    }, [children, isTextField, isTypography, ellipsisStyles, sx, text, textFieldProps, variant]);

    const shouldShowTooltip = !!showTooltip && !!tooltipContent;

    if (!shouldShowTooltip) {
        return (
            <>
                {children && <>{children}</>}
                {isTypography && (
                    <Typography variant={variant} sx={{ ...sx }}>
                        {text}
                    </Typography>
                )}
                {isTextField && <TextField {...textFieldProps} />}
            </>
        );
    }

    const commonProps = { title: tooltipContent, placement, 'data-testid': dataTestId, ...tooltipProps };
    const Wrapper = isStyledTooltip ? StyledTooltip : Tooltip;
    const wrapperProps = isStyledTooltip ? { ...commonProps, maxWidth: maxWidth || sx.maxWidth, tooltipSx, arrowSize } : { ...commonProps, arrow: true };

    return (
        <Wrapper {...wrapperProps}>
            <Box component="span">{getElement}</Box>
        </Wrapper>
    );
};

GenericTooltipText.propTypes = {
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    showTooltip: PropTypes.bool,
    tooltipContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    isTypography: PropTypes.bool,
    isTextField: PropTypes.bool,
    placement: PropTypes.oneOf([
        'bottom-end',
        'bottom-start',
        'bottom',
        'left-end',
        'left-start',
        'left',
        'right-end',
        'right-start',
        'right',
        'top-end',
        'top-start',
        'top',
    ]),
    maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tooltipSx: PropTypes.object,
    arrowSize: PropTypes.number,
    variant: PropTypes.string,
    ellipsis: PropTypes.bool,
    sx: PropTypes.object,
    tooltipProps: PropTypes.object,
    textFieldProps: PropTypes.object,
    children: PropTypes.node,
    dataTestId: PropTypes.string,
    isStyledTooltip: PropTypes.bool,
};

GenericTooltipText.defaultProps = {
    text: '',
    showTooltip: true,
    tooltipContent: '',
    isTypography: false,
    isTextField: false,
    placement: 'top',
    maxWidth: undefined,
    tooltipSx: undefined,
    arrowSize: 10,
    variant: 'body2',
    ellipsis: false,
    sx: {},
    tooltipProps: {},
    textFieldProps: {},
    children: null,
    dataTestId: 'generic-tooltip-text',
    isStyledTooltip: false,
};

export default React.memo(GenericTooltipText);
